import { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

/** Generate a stable session id for this chat (grouped in ai_assistant_chat_logs). */
function useSessionId() {
  const ref = useRef<string | null>(null);
  if (ref.current === null) ref.current = crypto.randomUUID();
  return ref.current;
}

/** Insert one turn (user message + AI response) into ai_assistant_chat_logs. Used by all assistants. */
async function logChatToDb(
  client: typeof supabase,
  entry: {
    user_id: string;
    brand_id: string;
    session_id: string;
    user_message: string;
    ai_response: string;
    function_calls?: unknown[];
    context_used?: Record<string, unknown>;
    response_time_ms?: number;
    tokens_used?: number;
    model_used?: string;
  }
) {
  const { error } = await client.from('ai_assistant_chat_logs').insert({
    user_id: entry.user_id,
    brand_id: entry.brand_id,
    session_id: entry.session_id,
    user_message: entry.user_message,
    ai_response: entry.ai_response,
    function_calls: (entry.function_calls ?? []) as unknown as import('@/integrations/supabase/types').Json,
    context_used: (entry.context_used ?? {}) as unknown as import('@/integrations/supabase/types').Json,
    response_time_ms: entry.response_time_ms ?? null,
    tokens_used: entry.tokens_used ?? null,
    model_used: entry.model_used ?? null,
  });
  if (error) throw error;
}

export interface AiMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  isTyping?: boolean;
  functionCalls?: Array<{
    function: string;
    arguments: any;
    result: any;
  }>;
}

export interface UseAiAssistantOptions {
  /** Edge function name to invoke (default: 'ai-assistant'). Use 'ai-assistant-sales' for Sales module. */
  invokeTarget?: string;
  /** Current page path (e.g. '/sales-operations/start-here') so the assistant can avoid self-reference. */
  currentPath?: string;
}

export const useAiAssistant = (options: UseAiAssistantOptions = {}) => {
  const { invokeTarget = 'ai-assistant', currentPath } = options;
  const sessionId = useSessionId();
  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFunctionCalling, setIsFunctionCalling] = useState(false);
  const { toast } = useToast();

  // Function to mark typing as complete
  const markTypingComplete = (messageId: string) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, isTyping: false } : msg
    ));
  };

  const sendMessage = async (content: string, brandId: string, userId: string) => {
    const userMessage: AiMessage = {
      id: Date.now().toString(),
      content,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Add typing indicator
    const typingMessage: AiMessage = {
      id: `typing-${Date.now()}`,
      content: '',
      isUser: false,
      timestamp: new Date(),
      isTyping: true,
    };
    setMessages(prev => [...prev, typingMessage]);

    const startTime = Date.now();

    try {
      // Build conversation history for context
      const conversationHistory = messages
        .filter(msg => !msg.isTyping)
        .map(msg => ({
          role: msg.isUser ? 'user' : 'assistant',
          content: msg.content
        }));

      const { data, error } = await supabase.functions.invoke(invokeTarget, {
        body: {
          message: content,
          brandId,
          userId,
          messages: conversationHistory,
          currentPath,
        },
      });

      if (error) {
        throw new Error(error.message || (typeof error === 'object' && (error as any).error) || 'Request failed');
      }

      // Function may return 200 with { error: "..." } in body
      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : (data.error as any)?.message || 'Request failed');
      }

      const responseText = data?.response ?? '';
      const responseTimeMs = Date.now() - startTime;

      // Universal logging: all assistant chats go to ai_assistant_chat_logs (fire-and-forget)
      logChatToDb(supabase, {
        user_id: userId,
        brand_id: brandId,
        session_id: sessionId,
        user_message: content,
        ai_response: responseText,
        function_calls: data?.functionCalls ?? [],
        context_used: { source: invokeTarget, currentPath: currentPath ?? undefined },
        response_time_ms: responseTimeMs,
        tokens_used: data?.tokens_used ?? undefined,
        model_used: data?.model_used ?? undefined,
      }).catch((err) => console.warn('AI chat log insert failed:', err));

      // Remove typing indicator
      setMessages(prev => prev.filter(msg => !msg.isTyping));

      // Check if function calls were made
      if (data?.functionCalls && data.functionCalls.length > 0) {
        setIsFunctionCalling(true);
        
        // Add a function call indicator message
        const functionCallMessage: AiMessage = {
          id: `function-${Date.now()}`,
          content: `🔍 Fetching your latest data...`,
          isUser: false,
          timestamp: new Date(),
          functionCalls: data.functionCalls,
        };
        
        setMessages(prev => [...prev, functionCallMessage]);
        
        // Brief delay to show function call indication
        setTimeout(() => {
          setIsFunctionCalling(false);
          
          // Replace with actual response
          setMessages(prev => prev.map(msg => 
            msg.id === functionCallMessage.id 
              ? {
                  ...msg,
                  content: responseText,
                  isTyping: true, // Start typing effect for response
                }
              : msg
          ));
        }, 500);
      } else {
        const aiMessage: AiMessage = {
          id: `ai-${Date.now()}`,
          content: responseText || "I couldn't generate a response.",
          isUser: false,
          timestamp: new Date(),
          isTyping: true, // Start with typing effect
        };

        setMessages(prev => [...prev, aiMessage]);
      }

    } catch (error) {
      const errMessage = error instanceof Error ? error.message : 'Failed to send message. Please try again.';
      console.error('Error sending message:', error);
      
      // Remove typing indicator
      setMessages(prev => prev.filter(msg => !msg.isTyping));
      setIsFunctionCalling(false);
      
      const errorMessage: AiMessage = {
        id: `error-${Date.now()}`,
        content: "I'm sorry, I encountered an error. Please try again.",
        isUser: false,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);

      toast({
        title: "Error",
        description: errMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearMessages = () => {
    setMessages([]);
  };

  return {
    messages,
    sendMessage,
    clearMessages,
    isLoading,
    isFunctionCalling,
    markTypingComplete,
  };
};